import React from 'react';
import { RoleTaskFull } from 'interfaces/roleTask.interface';
import RoleTaskTileForm from './roleTaskTileForm.component';
import { Ability, EquipmentIcon, MultiSelect, PlusIcon, Option, Skill } from '@umanforge/common';
import { AbilitiesIcon } from '@common_components/icons/abilities.icon';
import ValidationIndicator from '@common_components/others/validationIndicator.component';
import { ILUOSkill } from 'components/inputs/iluo_skill.component';
import ModalButton from '@common_components/buttons/modalButton.component';
import { useTranslation } from 'react-i18next';
import { useRoleTaskForm } from '../hook/roleTaskForm.hook';
import { Tooltip } from 'react-tooltip';

interface RoleTaskFormProps {
  taskId?: string;
  sectorId: string;
  onChange: ( roleTasks: RoleTaskFull[], hasChanged: boolean, isValid: boolean, requiredAbilities: string[], requiredSkills: { id: string; iluo: number | undefined }[] ) => void;
  equipmentSkillsOption: Option<Skill>[];
  equipmentAbilitiesOption: Option<Ability>[];
  fixed?: boolean;
}

const RoleTaskForm: React.FC<RoleTaskFormProps> = ( {
  sectorId,
  taskId,
  onChange,
  equipmentSkillsOption,
  equipmentAbilitiesOption,
  fixed = false,
} ) => {

  const { t } = useTranslation();

  const {
    handleAddRoleTask,
    handleRoleTaskDelete,
    handleRoleTaskChange,
    roleTasks,
    allowedSkills,
    allowedAbilities,
    sectorUsers,
    isAdmin,
    errors
  } = useRoleTaskForm(
    sectorId,
    taskId,
    onChange,
    equipmentSkillsOption,
    equipmentAbilitiesOption,
  );

  // Render
  return (
    <>
      <div>
        <div className='lg:flex gap-4 w-full'>
          <div className='w-full'>
            <MultiSelect
              label={ t( 'translation:authorizations_required_by_equipments' ) + ' (' + equipmentAbilitiesOption.length + ')' }
              disabled={ true }
              placeholder={ t( 'translation:no_associated_authorizations' ) }
              icon={ <AbilitiesIcon className='w-7 h-7' /> }
              clearable={ false }
              selectedValues={ equipmentAbilitiesOption }
              getValues={ equipmentAbilitiesOption }
              extra={ ability => {
                const isValid = roleTasks.some( rt => {
                  return rt.abilities.some( abl => {
                    return abl.id == ability.object.id;
                  } );
                } );

                return <div className='flex h-6' >
                  { !isValid &&
                <div className='relative w-2 h-5 pt-1 pl-0.5'>
                  <div className='relative w-1 h-3'>
                    <ValidationIndicator isRequired={true} isValid={false} />
                  </div>
                </div>
                  }
                </div>;
              } }
            />
          </div>

          <MultiSelect
            label={ t( 'translation:skills_required_by_equipments' ) + ' (' + equipmentSkillsOption.length + ')' }
            disabled={ true }
            placeholder={ t( 'translation:no_associated_skills' ) }
            icon={ <EquipmentIcon className='w-7 h-7' /> }
            clearable={ true }
            selectedValues={ equipmentSkillsOption }
            getValues={ equipmentSkillsOption }
            onChange={ undefined }
            extra={ skill => {
              const isValid = roleTasks.some( rt => {
                return rt.skills.some( skl => {
                  return skl.id == skill.object.id && skl.iluo >= ( skill.object.value ?? 1 );
                } );
              } );
              return <div className='flex'>
                <ILUOSkill
                  skill={ ( { ... skill.object, value: skill.object.value } ) }
                  onChange={ () => { /* empty */ } }
                />
                { !isValid &&
                  <div className='relative w-2 h-5 pt-1 pl-0.5'>
                    <div className='relative w-1 h-3'>
                      <ValidationIndicator isRequired={true} isValid={false} />
                    </div>
                  </div>
                }
              </div>;
            } }
          />
        </div>

        <div className='justify-end self-end max-2xl:justify-center flex items-center gap-2 max-2xl:mt-3'>
          <ModalButton
            key={'add_role_task'}
            label={ t( 'translation:add_role_task' ) }
            icon={<PlusIcon/>}
            textColor='#green-base'
            hoverBorderColor = 'hover:border-green-base'
            onClick={ handleAddRoleTask }
          />
          <>
            <span
              id={'roletask-info'}
              className={'ml-2 cursor-pointer flex items-center justify-center rounded-full bg-gray-200 w-6 h-6 text-gray-700 text-sm font-bold'}
            >
              i
            </span>
            <Tooltip
              anchorSelect={'#roletask-info'}
              className={'z-[1000] max-w-[400px] text-wrap'}
              place="top"
              positionStrategy='fixed'
            >
              <span>
                <span className="text-[14px] font-semibold underline underline-offset-2">{t( 'translation:role_info_title' )}</span>
                <p className="pl-2 pt-1">{t( 'translation:role_info_description' )}</p>
              </span>
            </Tooltip>
          </>
        </div>
      </div>
      <div key={errors?.users + ' ' + errors?.count + ' ' + errors?.equipmentAbilities + ' ' + errors?.equipmentSkills}>
        { errors?.users && <p className="text-red-500 h-6 mt-2">{ errors?.users }</p> }
        { errors?.count && <p className="text-red-500 h-6 mt-2">{ errors?.count }</p> }
        { errors?.equipmentAbilities && <p className="text-red-500 h-6 mt-2">{ errors?.equipmentAbilities }</p> }
        { errors?.equipmentSkills && <p className="text-red-500 h-6 mt-2">{ errors?.equipmentSkills }</p> }
      </div>

      <div>
        { roleTasks.map( ( roleTask, index ) => {
          return ( ( roleTask && sectorUsers ) ?
            <div key={roleTask.id + index + roleTasks.length} id={roleTask.id + index}>
              <RoleTaskTileForm
                isAdmin={isAdmin}
                onChange={ handleRoleTaskChange }
                onDelete={ handleRoleTaskDelete }
                roleTask={ roleTask }
                index={index}
                onILUOUpdate={( skillId: string, iluo:number ) => {
                  if ( roleTask.id === 'equipment_roletask' ) {
                    const equipIluo = equipmentSkillsOption.find( skill => { return skill.object.id == skillId; } )?.object.value ?? iluo;
                    if ( iluo < equipIluo ) {
                      iluo = equipIluo;
                    }
                  }
                  return iluo;
                }}
                skills={allowedSkills}
                abilities={allowedAbilities}
                sectorUsers={sectorUsers}
                fixed={fixed}
                canClose={ roleTasks.length > 1 }
              />
            </div>
            : null
          );
        } ) }
      </div>
    </>
  );
};

export default RoleTaskForm;
